// src/lib/api.ts

const API_BASE = 'https://2tlqfnnfk5.execute-api.eu-north-1.amazonaws.com'

// const API_BASE = import.meta.env.VITE_API_BASE_URL?.trim() || 'https://2tlqfnnfk5.execute-api.eu-north-1.amazonaws.com'

export class ApiError extends Error {
  status?: number
  data?: unknown
  constructor(message: string, status?: number, data?: unknown) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.data = data
  }
}

// --- Helper Functions ---

type JsonValue = any

async function parseJson(res: Response): Promise<JsonValue> {
  const text = await res.text()
  if (!text) return {}
  try {
    return JSON.parse(text)
  } catch {
    return { raw: text }
  }
}

function safeJsonParse(payload: string) {
  try {
    return JSON.parse(payload)
  } catch (error) {
    console.warn('Kon body niet parsen:', error)
    return null
  }
}

/**
 * Generic helper for making authenticated API calls.
 * Handles token, headers, and error parsing.
 */
async function authedFetch(
  token: string,
  endpoint: string,
  options: RequestInit = {},
): Promise<any> {
  const res = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
      Authorization: `Bearer ${token}`,
    },
  })
  const data = await parseJson(res)
  if (!res.ok) {
    const message = data?.detail || data?.message || `Fout bij aanroepen van ${endpoint}.`
    throw new ApiError(message, res.status, data)
  }
  return data
}


// --- Types ---

export type LoginResponse = {
  access_token: string
  token_type: string
  user?: { id: number | string; name: string; email: string }
  plan?: string
}

export type StartCheckoutInput = {
  email: string
  password: string
  plan_id: string
  coupon?: string | null
}

export type StartCheckoutResult = {
  checkoutUrl?: string
  sessionId?: string
  raw?: unknown
}

export type ProgressResponse = {
  completedLessons: number
  totalLessons: number
  recentScores: number[]
  lastExam: { name: string; score: number; date: string } | null
}

export type CourseResponse = { id: string; title: string; progress: number }

export type ExamResponse = { id: string; title: string; plan: string }

export type HistoryItem = {
  id: number;
  quiz_name: string;
  score: number;
  total_questions: number;
  completed_at: string; // ISO datetime string
}

export type UserProfile = {
  id: number;
  email: string;
  plan_id: string;
  created_at: string; // ISO date string

  // --- NEW FIELDS FOR PAUSE FEATURE ---
  has_used_pause: boolean;
  paused_at?: string | null;          // allow null
  access_started_at?: string | null;  // allow null
}

export type NotesQuota = {
  note_count: number
  total_bytes: number
  max_notes: number
  max_note_bytes: number
  max_total_bytes: number
}

export type OnlineNote = {
  note_id: string
  title: string
  content_json: Record<string, unknown>
  plain_text: string
  favorite: boolean
  trashed: boolean
  parent_id: string | null
  child_order: string[]
  payload_size_bytes: number
  client_updated_at: string
  server_updated_at: string
  deleted_at: string | null
}

export type SaveOnlineNoteInput = {
  title: string
  content_json: Record<string, unknown>
  plain_text: string
  favorite?: boolean
  trashed?: boolean
  parent_id?: string | null
  child_order?: string[]
  client_updated_at: string
}

export type BulkOnlineNoteInput = SaveOnlineNoteInput & {
  note_id: string
}

export type NotesResponse = {
  notes: OnlineNote[]
  quota: NotesQuota
}

export type NoteResponse = {
  note: OnlineNote
  quota: NotesQuota
}


// --- API Functions ---

export async function postLogin(body: { email: string; password: string }): Promise<LoginResponse> {
  const res = await fetch(`${API_BASE}/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })

  const data = (await parseJson(res)) as LoginResponse & { detail?: string; message?: string }

  if (res.ok && data?.access_token) {
    return data
  }

  const status = res.status
  let message = data?.detail || data?.message || 'Inloggen mislukt.'
  if (status === 401) message = 'Ongeldig e-mailadres of wachtwoord.'
  if (status === 403) message = 'Je account is (tijdelijk) gedeactiveerd.'
  if (status === 402) message = 'Je toegang is verlopen. Kies een pakket om te verlengen.'

  throw new ApiError(message, status, data)
}

export async function startCheckout(body: StartCheckoutInput): Promise<StartCheckoutResult> {
  const res = await fetch(`${API_BASE}/start-checkout`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      email: body.email,
      password: body.password,
      plan_id: body.plan_id,
      coupon: body.coupon ?? null,
    }),
  })

  const data = await parseJson(res)
  const nestedBody = typeof data?.body === 'string' ? safeJsonParse(data.body) : data?.body
  const url = nestedBody?.url || nestedBody?.checkout_url || data?.url || data?.checkout_url
  const sessionId =
    nestedBody?.sessionId ||
    nestedBody?.session_id ||
    data?.sessionId ||
    data?.session_id ||
    nestedBody?.id ||
    data?.id

  if (!res.ok) {
    const errMsg =
      nestedBody?.message ||
      nestedBody?.error ||
      data?.message ||
      data?.error ||
      (res.status === 400
        ? 'Email already exists. Please log in instead.'
        : 'Kon checkout niet starten.')
    throw new ApiError(String(errMsg), res.status, data)
  }

  if (!url && !sessionId) {
    throw new ApiError('Onverwachte reactie van de server bij het starten van de checkout.', res.status, data)
  }

  return {
    checkoutUrl: url ? String(url) : undefined,
    sessionId: sessionId ? String(sessionId) : undefined,
    raw: data,
  }
}

export type GiftCheckoutInput = {
  email: string
  plan_id: string
  coupon?: string | null
}

/**
 * Start a Stripe checkout for a GIFT purchase. The buyer only supplies their own
 * email (for the receipt + voucher delivery) — no account is created for them.
 */
export async function startGiftCheckout(body: GiftCheckoutInput): Promise<StartCheckoutResult> {
  const res = await fetch(`${API_BASE}/start-checkout`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      email: body.email,
      plan_id: body.plan_id,
      coupon: body.coupon ?? null,
      is_gift: true,
    }),
  })

  const data = await parseJson(res)
  const nestedBody = typeof data?.body === 'string' ? safeJsonParse(data.body) : data?.body
  const url = nestedBody?.url || nestedBody?.checkout_url || data?.url || data?.checkout_url
  const sessionId =
    nestedBody?.sessionId ||
    nestedBody?.session_id ||
    data?.sessionId ||
    data?.session_id ||
    nestedBody?.id ||
    data?.id

  if (!res.ok) {
    const errMsg =
      nestedBody?.message || nestedBody?.error || data?.message || data?.error || 'Kon checkout niet starten.'
    throw new ApiError(String(errMsg), res.status, data)
  }
  if (!url && !sessionId) {
    throw new ApiError('Onverwachte reactie van de server bij het starten van de checkout.', res.status, data)
  }

  return {
    checkoutUrl: url ? String(url) : undefined,
    sessionId: sessionId ? String(sessionId) : undefined,
    raw: data,
  }
}

// Note: gift redemption is app-only — there is intentionally no web redeem flow.
// The recipient redeems the voucher inside the Theorie101 mobile app.

// --- AI Leercoach ---
export type LeercoachTopic = {
  id: string
  seen: number
  correct: number
  mastery: number | null
}

export type LeercoachResponse = {
  has_data: boolean
  completed_exams: number
  total_answered: number
  overall_mastery: number | null
  topics: LeercoachTopic[]
  focus: string[]
  practice_question_ids: string[]
}

export async function getLeercoach(token: string): Promise<LeercoachResponse> {
  const res = await fetch(`${API_BASE}/user/leercoach`, {
    headers: { Authorization: `Bearer ${token}` },
  })
  const data = await parseJson(res)
  if (!res.ok) {
    throw new ApiError(data?.detail || data?.message || 'Kon de leercoach niet laden.', res.status, data)
  }
  return data as LeercoachResponse
}

export async function forgotPassword(body: { email: string }): Promise<string> {
  const res = await fetch(`${API_BASE}/forgotPassword`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  const data = await parseJson(res)
  if (res.ok) {
    return (data?.detail as string) || 'Er is een code naar je e-mail gestuurd.'
  }
  const message = data?.detail || data?.message || 'Er is een fout opgetreden bij het aanvragen van de code.'
  throw new ApiError(String(message), res.status, data)
}

export async function resetPassword(body: { email: string; code: string; new_password: string }): Promise<string> {
  const res = await fetch(`${API_BASE}/resetPassword`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  const data = await parseJson(res)
  if (res.ok) {
    return (data?.detail as string) || 'Je wachtwoord is succesvol gewijzigd. Je kunt nu inloggen.'
  }
  const message = data?.detail || data?.message || 'Ongeldige code of fout bij het instellen van het wachtwoord.'
  throw new ApiError(String(message), res.status, data)
}

export async function getProgress(token: string): Promise<ProgressResponse> {
  return authedFetch(token, '/progress')
}

export async function getCourses(token: string): Promise<CourseResponse[]> {
  return authedFetch(token, '/courses')
}

export async function getExams(token: string): Promise<ExamResponse[]> {
  return authedFetch(token, '/exams')
}

export async function getHistory(token: string): Promise<HistoryItem[]> {
  return authedFetch(token, '/quiz/quizzes/history');
}

export async function getProfile(token: string): Promise<UserProfile> {
  return authedFetch(token, '/user/profile/view');
}

export async function getNotes(token: string): Promise<NotesResponse> {
  return authedFetch(token, '/user/notes')
}

export async function saveNote(
  token: string,
  noteId: string,
  note: SaveOnlineNoteInput,
): Promise<NoteResponse> {
  return authedFetch(token, `/user/notes/${encodeURIComponent(noteId)}`, {
    method: 'PUT',
    body: JSON.stringify({
      favorite: false,
      trashed: false,
      parent_id: null,
      child_order: [],
      ...note,
    }),
  })
}

export async function bulkSaveNotes(token: string, notes: BulkOnlineNoteInput[]): Promise<NotesResponse> {
  return authedFetch(token, '/user/notes/bulk', {
    method: 'POST',
    body: JSON.stringify({ notes }),
  })
}

export async function deleteNote(token: string, noteId: string, clientUpdatedAt: string): Promise<{ quota: NotesQuota }> {
  return authedFetch(token, `/user/notes/${encodeURIComponent(noteId)}`, {
    method: 'DELETE',
    body: JSON.stringify({ client_updated_at: clientUpdatedAt }),
  })
}

export const apiBaseUrl = API_BASE
